import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetAvailabilityQueryKey,
  useGetAvailability,
} from '../../api/generated/endpoints/availability/availability'
import { useCreateBooking } from '../../api/generated/endpoints/bookings/bookings'
import {
  getGetCreditBalanceQueryKey,
  useGetCreditBalance,
} from '../../api/generated/endpoints/credits/credits'
import { useListRates } from '../../api/generated/endpoints/rates/rates'
import { SlotSummary } from '../../components/SlotSummary'
import { priceFor, saleFrom, saleParams, type Sale } from '../../lib/sale'
import { apiMessage, isConflict } from '../../lib/api-error'
import { isSlotFree, parseBookSearch } from '../../lib/slots'
import { formatHours } from '../../lib/credits'

/**
 * Confirm and book. Behind `_authed`, so the guard handles the logged-out case
 * for free: it redirects to /login with `redirect` set to this full href, and
 * login.tsx carries that through the magic-link email.
 *
 * Named /checkout rather than /book/confirm on purpose -- flat file routing
 * would otherwise make book.tsx a layout parent of it. It also reads correctly
 * in M2, when this becomes the page that hands off to Mercado Pago.
 */
export const Route = createFileRoute('/_authed/checkout')({
  validateSearch: parseBookSearch,
  component: CheckoutPage,
})

/** Remembered so a returning customer does not retype it. M5's admin work can
 *  replace this with a real profile endpoint. */
const PHONE_KEY = 'golf-bay-manager:phone'

function CheckoutPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [phone, setPhone] = useState(() => localStorage.getItem(PHONE_KEY) ?? '')
  const [name, setName] = useState('')
  const [useCredit, setUseCredit] = useState(false)

  const credit = useGetCreditBalance()
  // Only offer it when they have hours AND those hours can actually be spent.
  // With payments off a booking is already free, so the option would look
  // like a choice while doing nothing.
  const canUseCredit = (credit.data?.hours ?? 0) > 0 && (credit.data?.spendable ?? false)

  // The same sale /book showed, from the same URL, so the grid -- and the
  // price -- are the ones the customer chose from.
  const rates = useListRates({ query: { staleTime: 60_000 } })
  const sale = rates.data ? saleFrom(search, rates.data.rates, rates.data.coaches ?? []) : undefined
  const params = saleParams(sale)

  const availability = useGetAvailability(
    { date: search.date, ...params },
    // This is precisely the moment freshness matters, and it is free otherwise.
    { query: { refetchOnMount: 'always', enabled: !rates.isPending } },
  )

  const createBooking = useCreateBooking({
    mutation: {
      // NEVER auto-retry a create. A retried POST after a network blip is a
      // double booking -- and in M2, a double charge.
      retry: false,
      onSuccess: async (booking) => {
        localStorage.setItem(PHONE_KEY, phone)
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetAvailabilityQueryKey({ date: search.date }),
          }),
          queryClient.invalidateQueries({ queryKey: getGetCreditBalanceQueryKey() }),
        ])
        // With payments enabled the booking is only a HOLD until it is paid, so
        // the provider decides where the customer goes next. Off, there is no
        // checkout_url and the booking is already confirmed.
        if (booking.checkout_url) {
          window.location.href = booking.checkout_url
          return
        }
        // Straight to Upcoming -- they just created something upcoming.
        void navigate({ to: '/bookings', search: { tab: 'upcoming' } })
      },
      onError: (error) => {
        // Somebody beat us between render and submit. Refetch so whatever we
        // offer them instead is real.
        if (isConflict(error)) {
          void queryClient.invalidateQueries({
            queryKey: getGetAvailabilityQueryKey({ date: search.date }),
          })
        }
      },
    },
  })

  if (!search.start) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">La selección venció</h1>
        <p className="text-slate-600">Elegir un horario y seguimos desde ahí.</p>
        <Link to="/book" search={{ date: search.date }} className="text-slate-900 underline">
          Elegir un horario
        </Link>
      </div>
    )
  }

  const data = availability.data
  const slot = data?.starts.find((candidate) => candidate.start === search.start)
  const stillFree = slot ? isSlotFree(slot, search.bay) : undefined
  const bayName = search.bay ? data?.bays.find((b) => b.id === search.bay)?.name : undefined

  // What happens when they press the button. Three states, not two: this used
  // to branch on `useCredit` alone, so the no-credit branch was hardcoded to
  // the payments-OFF world and told every card-paying customer they would pay
  // at the venue -- immediately before redirecting them to Mercado Pago.
  //
  // Null while availability is still loading. Saying nothing for a moment
  // beats saying the wrong one and correcting it under their eyes.
  const payHint = useCredit
    ? 'Se descuenta una hora del saldo.'
    : !data
      ? null
      : data.payments_enabled
        ? 'Al confirmar se abre el pago en línea.'
        : 'Se paga en el local. Los pagos en línea llegan pronto.'

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    createBooking.mutate({
      data: {
        date: search.date,
        start: search.start!,
        bay_id: search.bay ?? null,
        phone,
        name: name.trim(),
        pay_with: useCredit ? 'credit' : 'money',
        ...params,
      },
    })
  }

  return (
    <div className="max-w-md space-y-6">
      <h1 className="text-2xl font-semibold">Confirmar la reserva</h1>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <SlotSummary
          date={search.date}
          start={search.start}
          slotMinutes={data?.slot_minutes ?? 60}
          priceUyuPesos={data ? priceFor(data, slot, search.bay) : 0}
          bayName={bayName}
          what={sale ? describeSale(sale) : undefined}
        />
      </div>

      {stillFree === false && (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
          <p className="font-medium text-amber-900">Ese horario se acaba de ocupar.</p>
          <Link
            to="/book"
            search={{ date: search.date, bay: search.bay }}
            className="mt-2 inline-block underline"
          >
            Elegir otro horario
          </Link>
        </div>
      )}

      {canUseCredit && (
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <input
            type="checkbox"
            checked={useCredit}
            onChange={(event) => setUseCredit(event.target.checked)}
            className="mt-0.5 size-5"
          />
          <span className="text-sm">
            <span className="font-medium">Usar una hora del saldo</span>
            <span className="block text-slate-600">
              Hay {formatHours(credit.data?.hours ?? 0)} en la cuenta. Usando una no hay nada que
              pagar.
            </span>
          </span>
        </label>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium">
            Celular
          </label>
          <p className="mt-0.5 text-xs text-slate-500">
            {/* Only promise the PIN where a lock exists to send one. Until
                then customers are let in by hand, and telling somebody a code
                is coming is telling them something untrue. */}
            {data?.door_access_enabled
              ? 'El PIN de la puerta llega a este número. Incluir el código de país.'
              : 'Incluir el código de país.'}
          </p>
          <input
            id="phone"
            type="tel"
            required
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+59899123456"
            pattern="\+[1-9][0-9]{7,14}"
            className="mt-1 min-h-12 w-full rounded-md border border-slate-300 px-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          />
        </div>

        <div>
          <label htmlFor="name" className="block text-sm font-medium">
            Nombre
          </label>
          <input
            id="name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 min-h-12 w-full rounded-md border border-slate-300 px-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          />
        </div>

        <button
          type="submit"
          disabled={createBooking.isPending || stillFree === false}
          className="min-h-12 w-full rounded-md bg-slate-900 px-4 text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {createBooking.isPending ? 'Reservando…' : 'Confirmar la reserva'}
        </button>

        <p className="text-center text-xs text-slate-500">{payHint}</p>
      </form>

      {createBooking.isError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
          <p className="text-red-900">
            {apiMessage(createBooking.error, "We couldn't create that booking. Please try again.")}
          </p>
          {isConflict(createBooking.error) && (
            <Link
              to="/book"
              search={{ date: search.date, bay: search.bay }}
              className="mt-2 inline-block underline"
            >
              Ver qué otros horarios hay
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

/** "2 a 4 personas, c/u · 3 personas" -- what is being bought, in words. */
function describeSale(sale: Sale): string {
  return `${sale.rate.name} · ${sale.people} ${sale.people === 1 ? 'persona' : 'personas'}`
}
